import * as Either from "fp-ts/lib/Either";
import { DateTime, Duration } from "luxon";
import type {
  DurationT as DurationSchemaT,
  PeriodT,
  RangeT,
} from "../../../../../../schema/period";
import { Duration as DurationSchema, Range } from "../../../../../../schema/period";
import type { SeriesOptionsType } from "highcharts";

const convertPeriodRange = (
  period: RangeT,
  datasetPeriodRange: { from: number; to: number },
): { from: number; to: number } => ({
  from:
    typeof period.from === "string"
      ? DateTime.fromISO(period.from as string).toMillis()
      : period.from || datasetPeriodRange.from,
  to:
    typeof period.to === "string"
      ? DateTime.fromISO(period.to as string).toMillis()
      : period.to || datasetPeriodRange.to,
});

const setExtremesFromSync = (
  axis: Highcharts.Axis,
  min: number | undefined,
  max: number | undefined,
) => {
  axis.setExtremes(min, max, undefined, false, { trigger: "SYNC" });
};

/**
 * @description
 * Set temporal axis extremes from selected period.
 * - If period is "ALL", set extremes to undefined.
 * - If period is "CYCLE", set extremes to cycle boundaries.
 * - If period is a duration, set lower extreme from to - duration.
 * - If period is a range, set extremes to the range boundaries.
 */
export const syncTemporalAxesPeriod = (
  axes: Highcharts.Axis[],
  selectedPeriod: PeriodT,
  datasetPeriodRange: { from: number; to: number },
  cycle?: { from: number; to: number },
) => {
  if (selectedPeriod === "ALL") {
    for (const axis of axes) setExtremesFromSync(axis, undefined, undefined);
    return;
  }

  if (selectedPeriod === "CYCLE" && !cycle) return;

  if (selectedPeriod === "CYCLE" && cycle) {
    for (const axis of axes) {
      const { dataMin, dataMax } = axis.getExtremes();
      setExtremesFromSync(axis, Math.max(dataMin, cycle.from), Math.min(dataMax, cycle.to));
    }
    return;
  }

  if (Either.isRight(DurationSchema.decode(selectedPeriod))) {
    for (const axis of axes) {
      const { max, dataMin, dataMax } = axis.getExtremes();
      const from = DateTime.fromMillis(max)
        .minus(Duration.fromISO(selectedPeriod as DurationSchemaT).toMillis())
        .toMillis();
      setExtremesFromSync(axis, Math.max(dataMin, from), Math.min(dataMax, max));
    }
    return;
  }

  if (Either.isRight(Range.decode(selectedPeriod))) {
    const period = selectedPeriod as RangeT;
    const { from, to } = convertPeriodRange(period, datasetPeriodRange);

    for (const axis of axes) setExtremesFromSync(axis, from, to);
  }
};

export const syncPointPeriod = (
  plot: Highcharts.Chart | null,
  points: Highcharts.Point[],
  datasetPeriodRange: { from: number; to: number },
  selectedPeriod: PeriodT,
  cycle?: { from: number; to: number },
) => {
  if (!plot) return;

  let interval: { from: number; to: number } = datasetPeriodRange;
  if (selectedPeriod === "ALL") {
    interval = datasetPeriodRange;
  } else if (cycle && selectedPeriod === "CYCLE") {
    interval = { from: cycle.from, to: cycle.to };
  } else if (Range.is(selectedPeriod)) {
    interval = convertPeriodRange(selectedPeriod, datasetPeriodRange);
  } else if (DurationSchema.is(selectedPeriod)) {
    interval = {
      from: DateTime.fromMillis(datasetPeriodRange.to)
        .minus(Duration.fromISO(selectedPeriod as DurationSchemaT).toMillis())
        .toMillis(),
      to: datasetPeriodRange.to,
    };
  }

  const { from, to } = interval;
  const visibleSeries = new Set();
  for (const point of points as (Highcharts.Point & { custom: { date: number } })[]) {
    const enabled = point.custom.date >= from && point.custom.date <= to;
    if (enabled) visibleSeries.add(point.series);
    point.update(
      {
        marker: {
          fillColor: point.options.marker?.fillColor,
          enabled,
        },
      },
      false,
      false,
    );
  }

  requestIdleCallback(() => {
    for (const serie of plot.series) {
      serie.update({ showInLegend: visibleSeries.has(serie) } as SeriesOptionsType, false);
    }
    plot.redraw(false);
  });
};
